import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trophy, ScanLine, CalendarDays, Clock, ArrowRight, Lock, Building2, Users } from 'lucide-react'
import { apiDelete, apiGet, apiPost } from '../api'
import { useFacility } from '../context/FacilityContext'
import { Alert, Button, EmptyState, Field, Loading, PageHeader, SectionHead, StatTile, StatusPill, initials } from '../components/ui'
import './pages.css'

// +4791720448 → +47 917 20 448
const fmtPhone = (p) => {
  const m = String(p).replace(/\s+/g, '').match(/^\+47(\d{3})(\d{2})(\d{3})$/)
  return m ? `+47 ${m[1]} ${m[2]} ${m[3]}` : p
}

const QUICK = [
  { to: '/tournaments', label: 'Administrer turneringer', icon: Trophy },
  { to: '/sales', label: 'Gå til salg', icon: ScanLine, soft: true },
  { to: '/activities', label: 'Se aktiviteter', icon: CalendarDays },
  { to: '/rentals', label: 'Utleie', icon: Clock }
]

async function readError(res, fallback) {
  try {
    const data = await res.json()
    return data.message || data.error || fallback
  } catch {
    return fallback
  }
}

function PartnerDashboard() {
  const { activeFacilityId, activeFacility, loading: facLoading, reload } = useFacility()
  const [stats, setStats] = useState({ tournaments: 0, ongoing: 0, open: 0, sales: 0, revenue: 0, redeemed: 0 })
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [confirmId, setConfirmId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [members, setMembers] = useState([])
  const [isOwner, setIsOwner] = useState(false)
  const [membersError, setMembersError] = useState('')
  const [removingId, setRemovingId] = useState(null)
  const [newAdminPhone, setNewAdminPhone] = useState('')
  const [adding, setAdding] = useState(false)
  const [addNotice, setAddNotice] = useState('')

  const loadMembers = async (facilityId) => {
    const [manageRes, statusRes] = await Promise.all([
      apiGet(`/api/facilities/${facilityId}/manage`),
      apiGet(`/api/facilities/${facilityId}/admin-status`)
    ])
    if (manageRes.ok) {
      const data = await manageRes.json()
      setMembers(data.members || [])
      setMembersLoaded(true)
    } else {
      setMembersError(await readError(manageRes, 'Kunne ikke hente administratorer'))
    }
    if (statusRes.ok) {
      const status = await statusRes.json()
      setIsOwner(status.isOwner === true)
    }
  }

  useEffect(() => {
    if (!activeFacilityId) return
    const load = async () => {
      setLoading(true)
      setMembersError('')
      setMembersLoaded(false)
      setConfirmId(null)
      setAddNotice('')
      setNewAdminPhone('')
      try {
        const [tRes, sRes] = await Promise.all([
          apiGet(`/api/tournaments/facility/${activeFacilityId}`),
          apiGet(`/api/facilities/${activeFacilityId}/sales`),
          loadMembers(activeFacilityId)
        ])
        const tournaments = tRes.ok ? await tRes.json() : []
        const sales = sRes.ok ? await sRes.json() : []
        const paid = sales.filter((s) => s.status === 'paid')
        setStats({
          tournaments: tournaments.length,
          ongoing: tournaments.filter((t) => t.status === 'ONGOING').length,
          open: tournaments.filter((t) => t.status === 'OPEN').length,
          sales: paid.length,
          revenue: paid.reduce((sum, s) => sum + Number(s.amount || 0), 0),
          redeemed: paid.filter((s) => s.redeemed).length
        })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [activeFacilityId])

  const ownerCount = members.filter((m) => m.role === 'OWNER').length

  const removeMember = async (member) => {
    setConfirmId(null)
    setRemovingId(member.id)
    setMembersError('')
    try {
      const res = await apiDelete(`/api/facilities/${activeFacilityId}/members/${member.id}`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke fjerne administrator'))
      await loadMembers(activeFacilityId)
      reload()
    } catch (err) {
      setMembersError(err.message || 'Kunne ikke fjerne administrator')
    } finally {
      setRemovingId(null)
    }
  }

  const addAdmin = async () => {
    const phone = newAdminPhone.trim()
    if (!phone || adding) return
    setAdding(true)
    setMembersError('')
    setAddNotice('')
    try {
      const res = await apiPost(`/api/facilities/${activeFacilityId}/members`, { phone, role: 'ADMIN' })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke legge til administrator'))
      const added = await res.json()
      setNewAdminPhone('')
      setAddNotice(`${added.userName} er lagt til som administrator.`)
      await loadMembers(activeFacilityId)
    } catch (err) {
      setMembersError(err.message || 'Kunne ikke legge til administrator')
    } finally {
      setAdding(false)
    }
  }

  if (facLoading) return <Loading />
  if (!activeFacility) {
    return (
      <div className="page">
        <EmptyState icon={Building2} title="Ingen anlegg tilgjengelig. Kontakt Spelaren for tilgang." />
      </div>
    )
  }

  const pending = stats.sales - stats.redeemed
  const tournamentSub = [stats.ongoing > 0 && `${stats.ongoing} pågår`, stats.open > 0 && `${stats.open} ${stats.open === 1 ? 'åpen' : 'åpne'}`]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="page">
      <PageHeader
        eyebrow="Oversikt"
        title={`Velkommen, ${activeFacility.name}`}
        subtitle="Oversikt over turneringer, produktsalg og aktiviteter. Bruk Salg for å skanne innløsningskoder."
      />

      <div className="stat-grid ov-stats">
        <StatTile as={Link} to="/tournaments" label="Turneringer" value={stats.tournaments} sub={tournamentSub || 'Ingen pågår'} />
        <StatTile as={Link} to="/sales" label="Produktsalg" value={stats.sales} sub="Betalte salg" />
        <StatTile as={Link} to="/sales" label="Omsetning" value={`${stats.revenue.toLocaleString('nb-NO')} kr`} sub="Betalte salg totalt" />
        <StatTile
          as={Link}
          to="/sales"
          label="Innløst"
          value={`${stats.redeemed}/${stats.sales}`}
          sub={pending > 0 ? `${pending} venter på henting` : 'Ingen venter på henting'}
        />
      </div>

      <div className="ov-grid">
        <section className="card" aria-labelledby="adm-h">
          <SectionHead
            id="adm-h"
            title="Administratorer"
            subtitle="Eier av arenaen kan legge til og fjerne administratorer. Siste eier kan ikke fjernes."
          />
          {membersError && <Alert tone="error">{membersError}</Alert>}
          {addNotice && <Alert tone="success">{addNotice}</Alert>}
          {members.length > 0 ? (
            <ul className="list">
              {members.map((member, i) => {
                const isLastOwner = member.role === 'OWNER' && ownerCount <= 1
                const phone = member.userPhone || member.phoneNumber || member.phone
                return (
                  <li key={member.id} className="ov-admin">
                    <div className="ov-admin-row">
                      <span className={`avatar ov-avatar a${i % 3}`} aria-hidden="true">{initials(member.userName)}</span>
                      <div className="ov-admin-main">
                        <span className="ov-admin-name">{member.userName}</span>
                        {phone && <span className="ov-admin-phone">{fmtPhone(phone)}</span>}
                      </div>
                      <StatusPill tone={member.role === 'OWNER' ? 'live' : 'neutral'}>
                        {member.role === 'OWNER' ? 'Eier' : 'Admin'}
                      </StatusPill>
                      {isOwner && !isLastOwner && (
                        <Button
                          variant="danger-outline"
                          size="sm"
                          aria-label={`Fjern ${member.userName}`}
                          aria-expanded={confirmId === member.id}
                          disabled={removingId === member.id}
                          onClick={() => setConfirmId(confirmId === member.id ? null : member.id)}
                        >
                          {removingId === member.id ? 'Fjerner…' : 'Fjern'}
                        </Button>
                      )}
                    </div>
                    {confirmId === member.id && (
                      <div className="ov-confirm" role="alertdialog" aria-label="Bekreft fjerning">
                        <span className="ov-confirm-text">
                          {`Fjerne ${member.userName} som administrator for ${activeFacility?.name || 'arenaen'}?`}
                        </span>
                        <div className="ov-confirm-actions">
                          <Button variant="tertiary" onClick={() => setConfirmId(null)}>Avbryt</Button>
                          <Button variant="danger" autoFocus onClick={() => removeMember(member)}>Fjern</Button>
                        </div>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          ) : (
            membersLoaded && (
              <EmptyState compact icon={Users}>Ingen administratorer registrert.</EmptyState>
            )
          )}
          {isOwner ? (
            <form
              className="ov-add-admin"
              onSubmit={(e) => {
                e.preventDefault()
                addAdmin()
              }}
            >
              <Field
                label="Legg til administrator"
                hint="Telefonnummeret personen bruker i Spelaren-appen. Personen må ha logget inn i appen minst én gang."
              >
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="912 34 567"
                  value={newAdminPhone}
                  onChange={(e) => setNewAdminPhone(e.target.value)}
                  disabled={adding}
                />
              </Field>
              <Button type="submit" variant="secondary" disabled={adding || !newAdminPhone.trim()}>
                {adding ? 'Legger til…' : 'Legg til'}
              </Button>
            </form>
          ) : (
            <div className="alert alert-note">
              <Lock size={18} strokeWidth={1.9} aria-hidden="true" />
              <span>Kun eier kan legge til og fjerne administratorer.</span>
            </div>
          )}
        </section>

        <section className="card" aria-labelledby="hv-h">
          <div className="row-between">
            <h2 id="hv-h" className="section-title">Hurtigvalg</h2>
            {loading && <span className="status-line" role="status">Oppdaterer tall…</span>}
          </div>
          <div className="ov-quick">
            {QUICK.map(({ to, label, icon: Icon, soft }) => (
              <Link key={to} to={to} className={`ov-quick-tile${soft ? ' soft' : ''}`}>
                <span className="ov-quick-icon"><Icon size={20} strokeWidth={1.9} aria-hidden="true" /></span>
                <span className="ov-quick-label">
                  {label}
                  <ArrowRight size={18} strokeWidth={1.9} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

export default PartnerDashboard
